import type { EvalVideo, GoldEventClip } from "@sitescene/types";

import { SubjectTrackOrigin } from "@sitescene/types";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { StatusMessage } from "@/components/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Input } from "@/components/ui/input/input";
import { TextArea } from "@/components/ui/textarea/textarea";
import { ROUTES, trackPath } from "@/constants/routes";
import { useAddClipsToGold, useEventClips } from "@/features/events/use-events";
import { GeneralModal } from "@/features/modals/general-modal";
import { useShadowEvent } from "@/features/reanalysis/use-reanalysis";
import { formatClock, TRACK_STATUS } from "@/features/tracking/format";
import { useEventTracks, useStartTrack } from "@/features/tracking/use-tracks";
import { formatWhen } from "@/pages/plausibility/format";

/**
 * One event: its cross-camera tracks, and its clips, one per camera clip,
 * with a way to add picked clips to the gold standard set.
 */
export const EventDetail = () => {
	const { eventId } = useParams();
	const event = useShadowEvent(eventId);
	const clips = useEventClips(eventId);
	// Lengths the browser read from each clip, for cameras that didn't report one
	const [durations, setDurations] = useState<Record<string, number>>({});
	const [adding, setAdding] = useState(false);
	const [added, setAdded] = useState<EvalVideo[] | null>(null);

	if (!event.data) {
		return (
			<StatusMessage
				error={event.error}
				isLoading={event.isLoading}
				loading="Loading the event…"
			/>
		);
	}

	const rows = clips.data ?? [];
	const available = rows.filter((clip) => !clip.alreadyInGold);

	return (
		<div className="flex flex-col gap-4">
			<DetailPanel title={event.data.title || "Untitled event"}>
				<KeyValueList
					items={[
						{ label: "Started", value: formatWhen(event.data.startTimestamp) },
						{ label: "Severity", value: event.data.severity },
						{
							label: "Status",
							value: (
								<Badge label={event.data.status} value={event.data.status} />
							),
						},
						{
							label: "Description",
							value: event.data.description || (
								<span className="text-tertiary">—</span>
							),
						},
					]}
				/>
			</DetailPanel>

			{eventId && <TracksPanel eventId={eventId} />}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<h3 className="text-sm font-semibold text-primary">
					Clips ({rows.length})
				</h3>
				<Button
					isDisabled={available.length === 0}
					onClick={() => {
						setAdded(null);
						setAdding(true);
					}}
					size="sm"
				>
					Add to gold standard
				</Button>
			</div>

			{added && (
				<p className="text-sm text-success-primary" role="status">
					Added {added.length} video{added.length === 1 ? "" : "s"} to the gold
					standard set.{" "}
					<Link className="underline" to={ROUTES.GLOBAL_GOLD_VIDEOS}>
						Open Videos
					</Link>{" "}
					to write their ground truth.
				</p>
			)}

			<StatusMessage
				empty="This event has no clips."
				error={clips.error}
				isEmpty={!clips.isLoading && rows.length === 0}
				isLoading={clips.isLoading}
				loading="Loading clips…"
			/>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
				{rows.map((clip) => (
					<ClipCard
						clip={clip}
						key={clip.mediaKey}
						onDuration={(seconds) =>
							setDurations((current) => ({
								...current,
								[clip.mediaKey]: seconds,
							}))
						}
					/>
				))}
			</div>

			{adding && eventId && (
				<AddToGoldModal
					clips={available}
					defaultSeverity={event.data.severity}
					durations={durations}
					eventId={eventId}
					onAdded={(videos) => {
						setAdded(videos);
						setAdding(false);
					}}
					onClose={() => setAdding(false)}
				/>
			)}
		</div>
	);
};

/**
 * The event's tracks, and a way to start one: it indexes every camera ±15
 * minutes around the event on the GPU, then follows the person from the
 * event's clips.
 */
const TracksPanel = ({ eventId }: { eventId: string }) => {
	const tracks = useEventTracks(eventId);
	const start = useStartTrack(eventId);
	const navigate = useNavigate();
	const rows = tracks.data ?? [];

	return (
		<DetailPanel title="Tracks">
			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<p className="text-sm text-tertiary">
						Follow a person from this event across the site's cameras, 15
						minutes either side. The GPU starts if it's off (a few minutes).
					</p>
					<Button
						isDisabled={start.isPending}
						onClick={() =>
							start.mutate(undefined, {
								onSuccess: (track) => void navigate(trackPath(track.id)),
							})
						}
						size="sm"
					>
						{start.isPending ? "Starting…" : "Track a person"}
					</Button>
				</div>
				{start.error && (
					<p className="text-sm text-error-primary" role="alert">
						{start.error.message}
					</p>
				)}
				<StatusMessage
					error={tracks.error}
					isLoading={tracks.isLoading}
					loading="Loading tracks…"
				/>
				{rows.length > 0 && (
					<ul className="flex flex-col divide-y divide-secondary text-sm">
						{rows.map((track) => {
							const status = TRACK_STATUS[track.status];
							return (
								<li
									className="flex flex-wrap items-center gap-3 py-2"
									key={track.id}
								>
									<Link
										className="font-medium underline"
										to={trackPath(track.id)}
									>
										{track.origin === SubjectTrackOrigin.Auto
											? "Auto track"
											: "Track"}{" "}
										started {formatClock(track.createdAt)}
									</Link>
									<Badge
										color={status.tone}
										label={status.label}
										value={track.status}
									/>
									<span className="text-tertiary">
										{track.hops.length} hop{track.hops.length === 1 ? "" : "s"}{" "}
										· {track.indexedClips.length} of {track.clips.length} clips
										checked
									</span>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</DetailPanel>
	);
};

const ClipCard = ({
	clip,
	onDuration,
}: {
	clip: GoldEventClip;
	onDuration: (seconds: number) => void;
}) => (
	<section className="flex flex-col gap-2 rounded-md border border-secondary p-3">
		<video
			className="aspect-video w-full rounded bg-gray-900"
			controls
			onLoadedMetadata={(event) => onDuration(event.currentTarget.duration)}
			preload="metadata"
			src={clip.url}
		/>
		<div className="flex items-start justify-between gap-2 text-sm">
			<div>
				<p className="font-medium text-primary">{clip.sensor.name}</p>
				<p className="text-tertiary">
					{clip.zone ? `${clip.zone.name} · ` : ""}
					{formatWhen(clip.startTime)}
				</p>
			</div>
			{clip.alreadyInGold && <Badge label="In gold set" value="completed" />}
		</div>
	</section>
);

interface AddToGoldModalProps {
	clips: GoldEventClip[];
	defaultSeverity: number;
	durations: Record<string, number>;
	eventId: string;
	onAdded: (videos: EvalVideo[]) => void;
	onClose: () => void;
}

/**
 * Pick clips, all ticked to start, and the fields every copy starts with.
 * Each copy also takes its camera's scene description and geometry.
 */
const AddToGoldModal = ({
	clips,
	defaultSeverity,
	durations,
	eventId,
	onAdded,
	onClose,
}: AddToGoldModalProps) => {
	const add = useAddClipsToGold(eventId);
	const [picked, setPicked] = useState(
		() => new Set(clips.map((clip) => clip.mediaKey)),
	);
	const [severity, setSeverity] = useState(String(defaultSeverity));
	const [description, setDescription] = useState("");

	const severityNumber = Number(severity);
	const severityValid =
		Number.isInteger(severityNumber) &&
		severityNumber >= 1 &&
		severityNumber <= 10;

	const toggle = (mediaKey: string, on: boolean) =>
		setPicked((current) => {
			const next = new Set(current);
			if (on) next.add(mediaKey);
			else next.delete(mediaKey);
			return next;
		});

	const submit = () =>
		add.mutate(
			{
				clips: clips
					.filter((clip) => picked.has(clip.mediaKey))
					.map((clip) => ({
						durationSeconds: clip.durationSeconds ?? durations[clip.mediaKey],
						mediaKey: clip.mediaKey,
					})),
				description: description.trim() || undefined,
				severity: severityNumber,
			},
			{ onSuccess: onAdded },
		);

	return (
		<GeneralModal
			closeOnSubmit={false}
			description="Each picked clip becomes its own gold standard video, with its camera's scene description and geometry."
			isOpen
			onClose={onClose}
			onSubmit={submit}
			submitDisabled={picked.size === 0 || !severityValid || add.isPending}
			submitText={
				add.isPending
					? "Adding…"
					: `Add ${picked.size} clip${picked.size === 1 ? "" : "s"}`
			}
			title="Add to gold standard"
		>
			<div className="flex flex-col gap-2">
				{clips.map((clip) => (
					<Checkbox
						hint={formatWhen(clip.startTime)}
						isSelected={picked.has(clip.mediaKey)}
						key={clip.mediaKey}
						label={clip.sensor.name}
						onChange={(on) => toggle(clip.mediaKey, on)}
					/>
				))}
			</div>

			<Input
				className="w-40"
				errorMessage={severityValid ? undefined : "1 to 10"}
				isInvalid={!severityValid}
				label="Severity (1–10)"
				onChange={setSeverity}
				type="number"
				value={severity}
			/>

			<TextArea
				hint="Optional. Leave blank to write each clip's ground truth on the Videos page."
				label="Ground truth"
				onChange={setDescription}
				rows={3}
				value={description}
			/>

			{add.error && (
				<p className="text-sm text-error-primary" role="alert">
					{add.error.message}
				</p>
			)}
		</GeneralModal>
	);
};
